// src/pages/Schedule/Schedule.js
// receiver schedule page — view/schedule/confirm approved pickups

import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  TableSortLabel,
  Tooltip,
} from "@mui/material";

import PickupsCalendar from "../../components/Schedule/PickupsCalendar";

import { useDarkMode } from "../../contexts/ThemeContext";
import { useTranslation } from "react-i18next";

import { useScheduleData } from "../../hooks/useScheduleData";
import { usePermissionGuard } from "../../hooks/usePermissionGuard";
import { useSnackbar } from "../../hooks/useSnackbar";

import { apiPostJson } from "../../api/client";

import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ActionButton from "../../components/ui/ActionButton";
import LastUpdatedBadge from "../../components/ui/LastUpdatedBadge";
import SearchBar from "../../components/ui/SearchBar";
import ViewToggle from "../../components/ui/ViewToggle";
import ScheduleEmptyState from "../../components/Schedule/ScheduleEmptyState";
import ScheduleActionDialog from "../../components/Schedule/ScheduleActionDialog";

import { faList, faCalendarAlt, faCalendarCheck, faClipboardCheck, faTimes, faCircleExclamation, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  getStatusChipProps,
  formatQuantity,
  formatDate,
  getDonorScheduleYMD,
  getDonorTimeWindow,
} from "../../utils/scheduleUtils";

import {
  buildScheduleCounts,
  buildCalendarEvents,
} from "../../utils/scheduleDerived";

import "../../style/MaterialManagement.css";
import "../../style/MaterialsAccordion.css";
import "../../style/StatusFilterTabs.css";
import "../../style/Schedule.css";
import "../../style/ScheduleCalendar.css";

function Schedule() {
  const { darkMode } = useDarkMode();
  const { t } = useTranslation();

  const { hasPermission, isChecking } = usePermissionGuard("receiver", "schedule");

  const [rowData, setRowData] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // unique key per row — requestId is unique, listing_id can repeat
  const rk = (r) => String(r.requestId || r.id);

  // page entrance animation (matches donor pattern)
  const [initialLoading, setInitialLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  // toolbar
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("table");

  // schedule dialog
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleError, setScheduleError] = useState("");
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);

  // confirm reception dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  // sorting
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("status");

  // pagination
  const [currentPage, setCurrentPage] = useState(0);
  const ROWS_PER_PAGE = 10;

  const { show: showSnackbar, el: snackbarEl } = useSnackbar();

  const {
    rows: scheduleRows,
    loading,
    error: errorMessage,
    lastUpdated,
    performRefresh,
  } = useScheduleData();

  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await performRefresh();
    } catch (e) {
      console.error("refresh error:", e);
    }
    setTimeout(() => setRefreshing(false), 2000);
  }, [performRefresh]);

  useEffect(() => {
    setRowData(scheduleRows);
    setSelectedIds(new Set());
  }, [scheduleRows]);

  const isLoading = loading || initialLoading;

  // 1500ms entrance delay (matches donor)
  useEffect(() => {
    const timer = setTimeout(() => setInitialLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  // trigger fade-in after loading completes
  useEffect(() => {
    if (!initialLoading && !loading) {
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    }
  }, [initialLoading, loading]);

  // counts derived from unfiltered rowData (for pill badges)
  const counts = useMemo(() => buildScheduleCounts(rowData), [rowData]);

  // sorting
  const getSortValue = useCallback(
    (row, key) => {
      const nullLast = (value) =>
        value === null || value === undefined || value === "" ? null : value;

      switch (key) {
        case "material":
          return nullLast(row.material)?.toString().toLowerCase() ?? null;

        case "status": {
          const s = row.status;
          if (!s) return null;
          const rank =
            s === "pending" ? 0 :
            s === "scheduled" ? 1 :
            s === "delivered" ? 2 :
            s === "late" ? 3 :
            99;
          return `${String(rank).padStart(2, "0")}::${s}`;
        }

        case "donor":
          return nullLast(row.donor)?.toString().toLowerCase() ?? null;

        case "scheduledDate":
          return nullLast(row.scheduledDate) ?? null;

        case "timeWindow":
          return nullLast(row.timeWindow)?.toString().toLowerCase() ?? null;

        case "dateReceived":
          return nullLast(row.dateReceived) ?? null;

        case "requestedQty":
          return row.requestedQty ?? null;

        case "allocatedQty":
          return row.allocatedQty ?? null;

        default:
          return nullLast(row[key]) ?? null;
      }
    },
    []
  );

  const compare = useCallback(
    (a, b) => {
      const aValue = getSortValue(a, orderBy);
      const bValue = getSortValue(b, orderBy);

      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;

      let result = 0;
      if (typeof aValue === "number" && typeof bValue === "number") {
        result = aValue - bValue;
      } else {
        result = String(aValue).localeCompare(String(bValue));
      }

      return order === "asc" ? result : -result;
    },
    [getSortValue, order, orderBy]
  );

  const sortedRowData = useMemo(() => {
    const rowsWithIndex = (rowData || []).map((row, index) => ({ row, index }));
    rowsWithIndex.sort((left, right) => {
      const result = compare(left.row, right.row);
      return result !== 0 ? result : left.index - right.index;
    });
    return rowsWithIndex.map((item) => item.row);
  }, [rowData, compare]);

  // filter pipeline: status filter → search filter (AND logic)
  const filteredRows = useMemo(() => {
    let rows = sortedRowData;

    if (statusFilter !== "all") {
      rows = rows.filter(
        (r) => r.status === statusFilter
      );
    }

    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      rows = rows.filter(
        (r) =>
          (r.material || "").toLowerCase().includes(q) ||
          (r.donor || "").toLowerCase().includes(q)
      );
    }

    return rows;
  }, [sortedRowData, statusFilter, searchTerm]);

  // calendar events respect active filters
  const calendarEvents = useMemo(() => buildCalendarEvents(filteredRows), [filteredRows]);

  // pagination
  const totalPages = Math.ceil(filteredRows.length / ROWS_PER_PAGE);
  const paginatedRows = useMemo(() => {
    const start = currentPage * ROWS_PER_PAGE;
    return filteredRows.slice(start, start + ROWS_PER_PAGE);
  }, [filteredRows, currentPage]);

  // reset page when filters/search change
  useEffect(() => {
    setCurrentPage(0);
  }, [statusFilter, searchTerm]);

  const handleRequestSort = useCallback(
    (property) => {
      setOrder((previousOrder) => {
        const isSameColumn = orderBy === property;
        return isSameColumn && previousOrder === "asc" ? "desc" : "asc";
      });
      setOrderBy(property);
    },
    [orderBy]
  );

  const makeSortHeader = useCallback(
    (label, key, align = "left") => (
      <TableCell className="mm-table__th" align={align} sortDirection={orderBy === key ? order : false}>
        <TableSortLabel
          active={orderBy === key}
          direction={orderBy === key ? order : "asc"}
          onClick={() => handleRequestSort(key)}
        >
          <strong>{label}</strong>
        </TableSortLabel>
      </TableCell>
    ),
    [handleRequestSort, order, orderBy]
  );

  // selection derived from filtered rows
  const selectedRows = useMemo(() => {
    if (!filteredRows?.length || selectedIds.size === 0) return [];
    const out = [];
    for (const r of filteredRows) {
      if (selectedIds.has(rk(r))) out.push(r);
    }
    return out;
  }, [filteredRows, selectedIds]);

  const hasSelection = selectedIds.size > 0;

  const canScheduleWithDonorInfo =
    selectedRows.length > 0 &&
    selectedRows.every((r) => r.status === "pending" && Boolean(getDonorScheduleYMD(r)));

  const canConfirmReception =
    selectedRows.length > 0 && selectedRows.every((r) => r.status === "scheduled" || r.status === "late");

  // date mismatch detection for schedule dialog
  const selectedDates = useMemo(() => {
    if (!canScheduleWithDonorInfo) return [];
    return [...new Set(selectedRows.map((r) => getDonorScheduleYMD(r)).filter(Boolean))];
  }, [canScheduleWithDonorInfo, selectedRows]);

  const hasMismatchedDates = selectedDates.length > 1;

  const updateRowsById = useCallback((idsSet, patch) => {
    setRowData((prev) =>
      (prev || []).map((r) =>
        idsSet.has(rk(r)) ? { ...r, ...patch } : r
      )
    );
  }, []);

  const handleToggleRow = useCallback((rowId, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const id = String(rowId);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const handleCancelSelection = useCallback(() => setSelectedIds(new Set()), []);

  // schedule dialog handlers
  const handleOpenSchedule = useCallback(() => {
    if (!canScheduleWithDonorInfo) return;
    setScheduleError("");
    setScheduleOpen(true);
  }, [canScheduleWithDonorInfo]);

  const handleApplySchedule = useCallback(async () => {
    setScheduleError("");
    if (selectedRows.length === 0) return;

    // group by donor date — backend takes a single date per call
    const byDate = new Map();
    for (const r of selectedRows) {
      const ymd = getDonorScheduleYMD(r);
      if (!ymd) continue;
      if (!byDate.has(ymd)) byDate.set(ymd, { date: ymd, time: getDonorTimeWindow(r) || null, ids: [] });
      byDate.get(ymd).ids.push(String(r.id));
    }

    if (byDate.size === 0) return;

    setScheduleSubmitting(true);
    try {
      for (const [, group] of byDate) {
        await apiPostJson("/material_management/requests/schedule", {
          listingIds: group.ids,
          scheduledDate: group.date,
          timeWindow: group.time,
        });
      }

      // optimistic update keyed by requestId
      const dateByRow = new Map();
      for (const r of selectedRows) {
        dateByRow.set(rk(r), getDonorScheduleYMD(r));
      }
      setRowData((prev) =>
        (prev || []).map((r) => {
          const d = dateByRow.get(rk(r));
          return d ? { ...r, status: "scheduled", scheduledDate: d } : r;
        })
      );

      setScheduleOpen(false);
      setSelectedIds(new Set());
      showSnackbar(
        t("schedule.snackbar.scheduled", "{{count}} pickup(s) confirmed successfully.", { count: selectedRows.length })
      );
      await performRefresh().catch(() => {
        showSnackbar(t("schedule.errors.refreshFailed", "Data may be stale — please refresh."), "warning");
      });
    } catch (e) {
      console.error("Schedule request error:", e);
      setScheduleError(
        t(
          "schedule.errors.scheduleFailed",
          "Failed to schedule pickups. Please try again."
        )
      );
    } finally {
      setScheduleSubmitting(false);
    }
  }, [selectedRows, t, performRefresh, showSnackbar]);

  const handleOpenConfirmReception = useCallback(() => {
    if (!canConfirmReception) return;
    setConfirmError("");
    setConfirmOpen(true);
  }, [canConfirmReception]);

  const handleCloseConfirmReception = useCallback(() => {
    if (confirmSubmitting) return;
    setConfirmOpen(false);
  }, [confirmSubmitting]);

  // day panel single-row actions
  const handleScheduleRow = useCallback((row) => {
    setSelectedIds(new Set([rk(row)]));
    setScheduleOpen(true);
  }, []);

  const handleConfirmRow = useCallback((row) => {
    setSelectedIds(new Set([rk(row)]));
    setConfirmOpen(true);
  }, []);

  const handleConfirmReceptionSubmit = useCallback(async () => {
    setConfirmError("");
    setConfirmSubmitting(true);

    const listingIds = selectedRows.map((r) => String(r.id));
    const rowKeys = new Set(selectedRows.map(rk));

    try {
      await apiPostJson("/material_management/requests/confirm-reception", { listingIds });

      updateRowsById(rowKeys, { status: "delivered" });
      setConfirmOpen(false);
      setSelectedIds(new Set());
      showSnackbar(
        t("schedule.snackbar.confirmed", "{{count}} pickup(s) confirmed as received.", { count: listingIds.length })
      );
      await performRefresh().catch(() => {
        showSnackbar(t("schedule.errors.refreshFailed", "Data may be stale — please refresh."), "warning");
      });
    } catch (e) {
      console.error("Confirm reception error:", e);
      setConfirmError(
        t(
          "schedule.errors.confirmFailed",
          "Failed to confirm reception. Please try again."
        )
      );
    } finally {
      setConfirmSubmitting(false);
    }
  }, [selectedRows, t, updateRowsById, performRefresh, showSnackbar]);

  const combinedErrorMessage = errorMessage || "";

  // select-all applies to current page only
  const allVisibleIds = useMemo(
    () => paginatedRows.map((r) => rk(r)),
    [paginatedRows]
  );

  const selectedCountVisible = useMemo(() => {
    let count = 0;
    for (const id of allVisibleIds) if (selectedIds.has(id)) count += 1;
    return count;
  }, [allVisibleIds, selectedIds]);

  const handleToggleAllVisible = useCallback(
    (checked) => {
      setSelectedIds(() => {
        if (!checked) return new Set();
        return new Set(allVisibleIds);
      });
    },
    [allVisibleIds]
  );

  // filter pill config
  const FILTER_PILLS = useMemo(() => [
    { key: "all", label: t("schedule.filters.all", "All"), count: rowData.length },
    { key: "pending", label: t("schedule.filters.pending", "Pending Your Confirmation"), count: counts.pending },
    { key: "scheduled", label: t("schedule.filters.scheduled", "Scheduled"), count: counts.scheduled },
    { key: "delivered", label: t("schedule.filters.received", "Received"), count: counts.delivered },
    { key: "late", label: t("schedule.filters.late", "Late"), count: counts.late },
  ], [t, rowData.length, counts]);

  const VIEW_TOGGLE_OPTS = useMemo(() => [
    { value: "table", label: t("schedule.views.table", "Table view"), icon: faList },
    { value: "calendar", label: t("schedule.views.calendar", "Calendar view"), icon: faCalendarAlt },
  ], [t]);

  // columns that toggle based on active filter tab
  const showCol = useMemo(() => {
    const f = statusFilter;
    return {
      scheduledDate: f !== "delivered",
      timeWindow: f !== "delivered",
      dateReceived: f === "all" || f === "delivered",
    };
  }, [statusFilter]);

  // dynamic colSpan = checkbox + material + status + donor + qty×2 + conditional cols
  const totalCols = 6 + (showCol.scheduledDate ? 1 : 0) + (showCol.timeWindow ? 1 : 0) + (showCol.dateReceived ? 1 : 0);

  if (isChecking) {
    return (
      <LoadingSpinner
        fullPage
        size={50}
        message={t("common.checkingPermissions", "Checking permissions...")}
      />
    );
  }

  if (!hasPermission) return null;

  if (isLoading) {
    return (
      <LoadingSpinner
        fullPage
        size={50}
        message={t("schedule.loading", "Fetching scheduled pickups...")}
      />
    );
  }

  return (
    <>
      {/* error banner with retry */}
      {combinedErrorMessage && (
        <div className="schedule-error-banner">
          <span className="schedule-error-banner__msg">{combinedErrorMessage}</span>
          <ActionButton
            variant="default"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {t("schedule.errors.retry", "Retry")}
          </ActionButton>
        </div>
      )}

      <Box
        className={`material-management-root schedule-root ${
          darkMode ? "dark" : "light"
        }${combinedErrorMessage ? " schedule-root--stale" : ""}${isVisible ? " schedule-root--visible" : ""}`}
        sx={{ mx: "auto", px: { xs: 2, md: 3 } }}
      >
        {/* page header with last updated badge (matches donor page-header-row) */}
        <div className="schedule-header-row">
          <div />
          <LastUpdatedBadge
            timestamp={lastUpdated}
            onRefresh={handleRefresh}
            isRefreshing={refreshing}
            variant="receiver"
          />
        </div>

        {/* empty state: no data at all */}
        {rowData.length === 0 && !combinedErrorMessage ? (
          <Box className="schedule-container">
            <div className="schedule-card">
              <ScheduleEmptyState />
            </div>
          </Box>
        ) : (

        <>
        {/* table card */}
        <Box className="schedule-container">
          <div className="schedule-card">
            {/* header bar (matches donor mat-header) */}
            <div className="mat-header">
              <span className="mat-title">
                {t("schedule.sectionTitle", "Schedule")}
                <span className="mat-count"> ({filteredRows.length})</span>
              </span>

              {hasSelection && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginLeft: 12 }}>
                  <span className="selection-badge">
                    {selectedIds.size} {t("schedule.toolbar.selected", "selected")}
                  </span>

                  {canScheduleWithDonorInfo && (
                    <ActionButton
                      variant="primary"
                      icon={faCalendarCheck}
                      iconOnly
                      tooltip={t("schedule.actions.schedule", "Confirm pickup")}
                      onClick={handleOpenSchedule}
                    />
                  )}

                  {canConfirmReception && (
                    <ActionButton
                      variant="danger"
                      icon={faClipboardCheck}
                      iconOnly
                      tooltip={t("schedule.actions.confirmReception", "Confirm reception")}
                      onClick={handleOpenConfirmReception}
                    />
                  )}

                  {!canScheduleWithDonorInfo && !canConfirmReception && (() => {
                    const statuses = new Set(selectedRows.map((r) => r.status));
                    const allPending = statuses.size === 1 && statuses.has("pending");
                    const allDelivered = statuses.size === 1 && statuses.has("delivered");
                    const msg = allPending
                      ? t("schedule.toolbar.missingDate", "Missing donor pickup date — cannot schedule")
                      : allDelivered
                      ? t("schedule.toolbar.noActions", "No actions available for received items")
                      : t("schedule.toolbar.mixedHint", "Select rows with the same status to act");
                    return <span className="selection-hint">{msg}</span>;
                  })()}

                  <ActionButton
                    variant="default"
                    icon={faTimes}
                    iconOnly
                    tooltip={t("schedule.actions.clearSelection", "Clear selection")}
                    onClick={handleCancelSelection}
                  />
                </div>
              )}

              <div className="mat-spacer" />

              <div className={`status-filter-tabs ${darkMode ? "dark" : "light"}`}>
                {FILTER_PILLS.map((pill) => (
                  <button
                    key={pill.key}
                    className={`status-filter-tab${statusFilter === pill.key ? " active" : ""}`}
                    onClick={() => {
                      setStatusFilter(pill.key);
                      setSelectedIds(new Set());
                    }}
                  >
                    {pill.label}
                    <span className="status-filter-count">{pill.count}</span>
                  </button>
                ))}

              </div>

              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                darkMode={darkMode}
                size="small"
                placeholder={t("schedule.toolbar.search", "Search material or donor...")}
              />

              <ViewToggle
                options={VIEW_TOGGLE_OPTS}
                value={viewMode}
                onChange={setViewMode}
                darkMode={darkMode}
              />
            </div>

            <div className="schedule-view-pane">
            {viewMode === "table" && (
            <>
            <Box className="mm-table">
          <Box className="mm-table__wrap">
            <Table size="small" className="mm-table__table">
              <TableHead>
                <TableRow className="mm-table__tr mm-table__tr--head">
                  <TableCell className="mm-table__th" padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={
                        allVisibleIds.length > 0 &&
                        selectedCountVisible === allVisibleIds.length
                      }
                      indeterminate={
                        selectedCountVisible > 0 &&
                        selectedCountVisible < allVisibleIds.length
                      }
                      onChange={(event) =>
                        handleToggleAllVisible(event.target.checked)
                      }
                    />
                  </TableCell>

                  {makeSortHeader(
                    t("schedule.table.materialType", "Material Type"),
                    "material"
                  )}
                  {makeSortHeader(t("schedule.table.status", "Status"), "status")}
                  {makeSortHeader(t("schedule.table.donor", "Donor"), "donor")}
                  {showCol.scheduledDate && makeSortHeader(
                    t("schedule.table.scheduledDate", "Scheduled Date"),
                    "scheduledDate"
                  )}
                  {showCol.timeWindow && makeSortHeader(t("schedule.table.time", "Time"), "timeWindow")}
                  {showCol.dateReceived && makeSortHeader(
                    t("schedule.table.dateReceived", "Date Received"),
                    "dateReceived"
                  )}
                  {makeSortHeader(
                    t("schedule.table.requestedQty", "Requested Qty"),
                    "requestedQty",
                    "right"
                  )}
                  {makeSortHeader(
                    t("schedule.table.allocatedQty", "Allocated Qty"),
                    "allocatedQty",
                    "right"
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedRows.map((row) => {
                  const rowId = rk(row);
                  const selected = selectedIds.has(rowId);
                  const chipProps = getStatusChipProps(row.status);

                  return (
                    <TableRow
                      key={rowId}
                      hover
                      className={`mm-table__tr mm-table__tr--body${selected ? " is-selected" : ""}`}
                      onClick={(e) => {
                        // skip if click originated from the checkbox itself
                        if (e.target.closest('input[type="checkbox"]')) return;
                        handleToggleRow(rowId, !selected);
                      }}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell className="mm-table__td" padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selected}
                          onChange={(event) =>
                            handleToggleRow(rowId, event.target.checked)
                          }
                        />
                      </TableCell>

                      <TableCell className="mm-table__td mm-table__td--strong">{row.material}</TableCell>

                      <TableCell className="mm-table__td">
                        <Chip
                          size="small"
                          label={
                            chipProps.hasAlert
                              ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                  {t(chipProps.labelKey, chipProps.fallback)}
                                  <FontAwesomeIcon icon={faCircleExclamation} style={{ fontSize: "0.7rem" }} />
                                </span>
                              : t(chipProps.labelKey, chipProps.fallback)
                          }
                          variant={chipProps.variant}
                          color={chipProps.color}
                          sx={{ fontWeight: 900, letterSpacing: "0.02em" }}
                        />
                      </TableCell>

                      <TableCell className="mm-table__td">{row.donor}</TableCell>
                      {showCol.scheduledDate && (
                        <TableCell className="mm-table__td">
                          {row.status === "pending" ? (
                            <Tooltip title={t("schedule.table.pendingDateHint", "Pickup date — pending your confirmation")} arrow placement="top">
                              <span style={{ opacity: 0.55, display: "inline-flex", alignItems: "center", gap: 5 }}>
                                {formatDate(row.scheduledDate)}
                                <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: "0.7rem" }} />
                              </span>
                            </Tooltip>
                          ) : (
                            formatDate(row.scheduledDate)
                          )}
                        </TableCell>
                      )}
                      {showCol.timeWindow && <TableCell className="mm-table__td">{row.timeWindow}</TableCell>}
                      {showCol.dateReceived && <TableCell className="mm-table__td">{formatDate(row.dateReceived)}</TableCell>}

                      <TableCell className="mm-table__td" align="right">
                        {formatQuantity(row.requestedQty)}
                      </TableCell>
                      <TableCell className="mm-table__td" align="right">
                        {formatQuantity(row.allocatedQty)}
                      </TableCell>
                    </TableRow>
                  );
                })}

                {filteredRows.length === 0 && (
                  <TableRow>
                    <TableCell className="mm-table__td" colSpan={totalCols} align="center" sx={{ py: 3 }}>
                      {searchTerm || statusFilter !== "all"
                        ? t("schedule.noResults", "No pickups match the current filters.")
                        : t("schedule.empty", "No approved pickups found.")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Box>

            {/* pagination */}
            {filteredRows.length > ROWS_PER_PAGE && (
              <div className="mm-pagination" style={{ padding: "12px 16px" }}>
                <span className="mm-pagination__count">
                  {t("schedule.pagination.showing", "Showing {{start}}–{{end}} of {{total}}", {
                    start: currentPage * ROWS_PER_PAGE + 1,
                    end: Math.min((currentPage + 1) * ROWS_PER_PAGE, filteredRows.length),
                    total: filteredRows.length,
                  })}
                </span>
                <div className="mm-pagination__controls">
                  <button
                    className="mm-pagination__btn"
                    onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                    disabled={currentPage === 0}
                  >
                    ‹
                  </button>
                  <span className="mm-pagination__info">
                    {currentPage + 1} / {totalPages}
                  </span>
                  <button
                    className="mm-pagination__btn"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={currentPage >= totalPages - 1}
                  >
                    ›
                  </button>
                </div>
              </div>
            )}
            </>
            )}

            {viewMode === "calendar" && (
              <PickupsCalendar
                darkMode={darkMode}
                events={calendarEvents}
                onScheduleRow={handleScheduleRow}
                onConfirmRow={handleConfirmRow}
              />
            )}
            </div>
          </div>
        </Box>

        <ScheduleActionDialog
          mode="schedule"
          open={scheduleOpen}
          onClose={() => setScheduleOpen(false)}
          onConfirm={handleApplySchedule}
          submitting={scheduleSubmitting}
          error={scheduleError}
          selectedRows={selectedRows}
          confirmDisabled={!canScheduleWithDonorInfo}
          hasMismatchedDates={hasMismatchedDates}
          selectedDates={selectedDates}
        />

        <ScheduleActionDialog
          mode="confirm"
          open={confirmOpen}
          onClose={handleCloseConfirmReception}
          onConfirm={handleConfirmReceptionSubmit}
          submitting={confirmSubmitting}
          error={confirmError}
          selectedRows={selectedRows}
          confirmDisabled={confirmSubmitting || selectedRows.length === 0}
        />

        </>
        )}
      </Box>

      {snackbarEl}
    </>
  );
}

export default Schedule;
